import { useMutation, useQuery as useGQLQuery } from '@apollo/client';
import { CloudDownload, CreateNewFolder, Delete, Edit } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Fab,
  Grid,
  Icon,
  Link,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Tooltip,
  Typography
} from '@mui/material';
import { Box } from '@mui/system';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import DELETE_MODULE from 'gql/mutations/ModulesService/deleteModule.gql';
import DELETE_MODULE_SELECTION from 'gql/mutations/ModulesService/deleteModuleSelection.gql';
import DELETE_MODULES_BY_SELECTION from 'gql/mutations/ModulesService/deleteModulesBySelection.gql';
import UPDATE_TAKING_PLACE from 'gql/mutations/ModulesService/updateTakingPlace.gql';
import QUERY_GROUPINGS from 'gql/queries/ModulesService/getGroupings.gql';
import MODULES_SELECTION_QUERY from 'gql/queries/ModulesService/getModuleSelectionById.gql';

import { invalidateModulesQuery, useModulesQuery } from 'api/hooks/modules';
import { useCoursesQuery } from 'api/hooks/useCoursesQuery';
import { DateBox } from 'app/components';
import { EntryWithHeadline } from 'app/components';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import ContextMenu from 'app/components/ContextMenu';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { Filter } from 'app/components/Filters';
import GQLGuard from 'app/components/GQLGuard';
import { Modal } from 'app/components/Modal';
import { ModuleItem, States } from 'app/components/RichList/items/AWPM-FWPM/ModuleItem';
import { getModuleLecturer } from 'app/components/RichList/items/AWPM-FWPM/ModuleItem/helper';
import { TilesOrListView, TypedGridColumns } from 'app/components/TilesOrListView';
import { handleDownload } from 'app/components/Util/downloadAttachement';
import { WatchlistAddButton } from 'app/components/WatchlistAddButton';
import ModuleModal from 'app/containers/AWPM-FWPM/Modules/ModuleModal';
import { ModuleWithComplete } from 'app/containers/AWPM-FWPM/Modules/models';
import { getSeatsForModules } from 'app/containers/AWPM-FWPM/helper';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { SortOption } from 'types/AppTypes';
import { ACLHelper } from 'utils/ACLHelper';
import { useCurrentURL } from 'utils/hooks/useCurrentURL';

import { SelectionTabPanels } from '..';
import ModuleSelectionModal from '../../ModuleSelectionModal/ModuleSelectionModal';
import { hasAtLeastStarted, thisModuleSelectionIsDeleteable } from '../../helper';
import { ModuleSelection, State, SubjectType } from '../../models';
import { AddModuleToSelectionModal } from '../components/AddModuleToSelectionModal';
import { ExplanatoryLegend } from '../components/ExplanatoryLegend';
import SelectionAdminButtons from '../components/SelectionAdminButtons';
import '../styles/ModuleSelectionsDetail.scss';

type Props = {
  moduleSelection: ModuleSelection;
  subjectType: SubjectType;
  isSelectionAdmin: boolean;
  currentUser?: any;
  setTab: (value: SelectionTabPanels) => void;
};

export function PreviewTab({
  moduleSelection,
  subjectType,
  isSelectionAdmin,
  currentUser,
  setTab
}: Props) {
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [asTemplate, setAsTemplate] = useState(false);
  const [addModuleSelectionModal, setAddModuleSelectionModal] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [currentlyEditingModule, setCurrentlyEditingModule] = useState<
    ModuleWithComplete | undefined
  >(undefined);
  const titleInputState = useState('');
  const takesPlaceInputState = useState<null | boolean>(null);
  const completionInputState = useState('');

  const [isNotTakingPlaceModalOpen, setIsNotTakingPlaceModalOpen] = useState(false);
  const groupingInputState = useState([]);

  const queryClient = useQueryClient();

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentURL = useCurrentURL();
  const aclHelper = new ACLHelper(currentUser);

  const isLecturer = !isSelectionAdmin;

  const { data: modules, isLoading } = useModulesQuery({
    params: {
      selectionId: moduleSelection.id,
      lecturerId: isLecturer ? currentUser.id.toString() : undefined,
      nameSort: 'asc',
      name: titleInputState[0],
      isTakingPlace: takesPlaceInputState[0],
      completionState: completionInputState[0],
      groupings: groupingInputState[0]
    }
  });

  const { data: courses } = useCoursesQuery();

  const adminCourses = aclHelper.getSelectionAdminCourses(courses);
  const allowedToEdit: boolean =
    isSelectionAdmin && subjectType === SubjectType.Fwpm && moduleSelection.courseId
      ? aclHelper.isSuperUser()
        ? true
        : adminCourses.map(course => course.id).includes(moduleSelection.courseId)
      : aclHelper.isSubjectSelectionAdmin(false, SubjectType.Awpm);
  const isSelectionEditable: boolean = moduleSelection.state !== State.Archived && allowedToEdit;

  const [deleteSelection] = useMutation(DELETE_MODULE_SELECTION, {
    context: { clientName: 'federation' }
  });

  const [deleteModules] = useMutation(DELETE_MODULES_BY_SELECTION, {
    context: { clientName: 'federation' }
  });

  const [updateTakingPlace] = useMutation(UPDATE_TAKING_PLACE, {
    context: { clientName: 'federation' }
  });

  const getGroupings = useGQLQuery<any, any>(QUERY_GROUPINGS, {
    context: { clientName: 'federation' }
  });

  const [deleteModule] = useMutation(DELETE_MODULE, {
    context: { clientName: 'federation' }
  });

  const cancelModuleSelectionCreation = (event?, reason?) => {
    if (disabledCancel || (reason && reason === 'backdropClick')) {
      return;
    }
    setDisabledCancel(true);
    setUpdateModalOpen(false);
    setDisabledCancel(false);
  };

  const openTemplateModal = () => {
    setAsTemplate(true);
    setUpdateModalOpen(true);
  };

  const openModalHandler = () => {
    setAsTemplate(false);
    setUpdateModalOpen(true);
  };

  const handleChange = (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleDeleteSelection = async () => {
    try {
      await deleteModules({
        variables: { id: moduleSelection.id }
      });
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.modules.alerts.deleteFailed())));
      return;
    }
    try {
      await deleteSelection({
        variables: { id: parseInt(moduleSelection.id, 10) }
      });
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.modules.alerts.deleteFailed())));
      return;
    }
    navigate(`/${subjectType.toLowerCase()}/module-selections`);
  };

  const handleDelete = async () => {
    const { errors } = await deleteModule({
      variables: {
        id: Number(currentlyEditingModule?.id)
      }
    });
    invalidateModulesQuery(queryClient);
    if (errors) {
      dispatch(AlertActions.error(t(translations.pages.modules.alerts.deleteFailed())));
    } else {
      dispatch(AlertActions.success(t(translations.pages.modules.alerts.deleteSuccessful())));
      invalidateModulesQuery(queryClient);
    }
  };

  const cancelModuleEditing = (_event?, reason?) => {
    if (disabledCancel || (reason && reason === 'backdropClick')) {
      return;
    }
    setCurrentlyEditingModule(undefined);
    setDisabledCancel(true);
    setEditModalOpen(false);
    setDisabledCancel(false);
    invalidateModulesQuery(queryClient);
  };

  const handleChangeIsTakingPlace = useCallback(
    async (module = currentlyEditingModule) => {
      if (module) {
        const { errors } = await updateTakingPlace({
          variables: {
            id: Number(module.id),
            state: !module.isTakingPlace
          }
        });
        invalidateModulesQuery(queryClient);
        if (errors) {
          dispatch(AlertActions.error(t(translations.pages.modules.alerts.changeStateFailed())));
        } else {
          dispatch(
            AlertActions.success(t(translations.pages.modules.alerts.changeStateSuccssful()))
          );
        }
      }
    },
    [currentlyEditingModule, dispatch, t, updateTakingPlace, queryClient]
  );

  const displayStates = state => {
    switch (state) {
      case State.Dischargable:
        return t(translations.pages.moduleSelections.states.dischargable());
      case State.Active:
        return t(translations.pages.moduleSelections.states.active());
      case State.Archived:
        return t(translations.pages.moduleSelections.states.archived());
      case State.Completed:
        return t(translations.pages.moduleSelections.states.completed());
      case State.Draft:
        return t(translations.pages.moduleSelections.states.draft());
      case State.Started:
        return t(translations.pages.moduleSelections.states.started());
      default:
        return t(translations.pages.moduleSelections.states.inactive());
    }
  };

  const rowActions = useCallback(
    params => {
      return [
        <GridActionsCellItem
          icon={
            <Icon
              className="fa fa-pen"
              style={{
                width: 'auto',
                color: 'lightblue'
              }}
            />
          }
          label={t(translations.common.edit())}
          onClick={() => {
            setCurrentlyEditingModule(params.row);
            setEditModalOpen(true);
          }}
          showInMenu
        />,
        <GridActionsCellItem
          icon={
            <Icon
              className={`fa fa-${!!params.row.isTakingPlace ? 'minus' : 'check'}-circle`}
              style={{
                width: 'auto',
                color: !!params.row.isTakingPlace ? 'orange' : 'green'
              }}
            />
          }
          label={
            !!params.row.isTakingPlace
              ? t(translations.pages.modules.isNotTakingPlaceButton())
              : t(translations.pages.modules.takesPlaceButton())
          }
          onClick={() => {
            setCurrentlyEditingModule(params.row);
            if (!!params.row.isTakingPlace) {
              setIsNotTakingPlaceModalOpen(true);
            } else {
              handleChangeIsTakingPlace(params.row);
            }
          }}
          showInMenu
        />,
        <GridActionsCellItem
          icon={
            <Icon
              className="far fa-trash-alt"
              style={{
                width: 'auto',
                color: 'red'
              }}
            />
          }
          label={t(translations.common.delete())}
          onClick={() => {
            setCurrentlyEditingModule(params.row);
            setDeleteModalOpen(true);
          }}
          showInMenu
          disabled={hasAtLeastStarted(moduleSelection)}
        />
      ];
    },
    [handleChangeIsTakingPlace, t, moduleSelection]
  );

  const checkState = (module: ModuleWithComplete) => {
    if (!module?.isTakingPlace && !module?.isDefault) {
      return States.IsNotTakingPlace;
    } else if (module?.complete) {
      return States.Complete;
    } else if (module?.isDefault) {
      return States.Draft;
    } else {
      return States.Incomplete;
    }
    // TODO use Flag to determine if the selection is finished
    // if (module?.isTakingPlace === true) {
    //   return States.TookPlace;
    // } else {
    //   return States.DidNotTakePlace;
    // }
  };

  const columns = useMemo((): TypedGridColumns<ModuleWithComplete> => {
    return [
      {
        field: 'complete',
        flex: 2,
        headerName: t(translations.pages.modules.status()),
        valueGetter(value, row) {
          return t(translations.pages.modules[checkState(row)]());
        }
      },
      {
        flex: 3,
        minWidth: 200,
        headerName: t(translations.pages.modules.create.modulePreview.title()),
        field: 'name',
        renderCell: params => (
          <Tooltip title={params.row.name}>
            <span>{params.row.name}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.pages.modules.lecturer()),
        field: 'lecturersUsers',
        renderCell: params => (
          <Tooltip title={getModuleLecturer(params.row, t)}>
            <span>{getModuleLecturer(params.row, t)}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.pages.modules.grouping()),
        field: 'grouping',
        valueGetter: (value, row) => row.grouping?.name,
        renderCell: params => (
          <Tooltip title={params.row.grouping?.name}>
            <span>{params.row.grouping?.name}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.pages.modules.seatsAvailable()),
        field: 'maxParticipants',
        renderCell: params => (
          <Tooltip title={params.row.maxParticipants}>
            <>
              <span className="module__footer__seats">{params.row.maxParticipants}</span>
              <div style={{ whiteSpace: 'normal' }}>{getSeatsForModules(params, courses, t)}</div>
            </>
          </Tooltip>
        )
      },
      {
        flex: 1,
        minWidth: 100,
        headerName: t(translations.common.actions()),
        field: 'actions',
        type: 'actions',
        getActions: rowActions
      }
    ];
  }, [t, rowActions, courses]);

  const richlistFilter = (groupings: any): Filter[] => [
    {
      field: 'name',
      label: t(translations.pages.moduleSelectionsDetail.filter.search()),
      stateHandler: titleInputState
    },
    {
      field: 'isTakingPlace',
      label: t(translations.pages.moduleSelectionsDetail.filter.takesPlace()),
      select: {
        options: [
          { label: '---', name: null },
          {
            label: t(translations.pages.moduleSelectionsDetail.filter.options.takesPlace()),
            name: true
          },
          {
            label: t(translations.pages.moduleSelectionsDetail.filter.options.takesNotPlace()),
            name: false
          }
        ],
        stateHandler: takesPlaceInputState
      }
    },

    {
      field: 'completionState',
      label: t(translations.pages.moduleSelectionsDetail.filter.completed()),
      select: {
        options: [
          { label: '---', name: null },
          {
            label: t(translations.pages.moduleSelectionsDetail.filter.options.complete()),
            name: 'completed'
          },
          {
            label: t(translations.pages.moduleSelectionsDetail.filter.options.incomplete()),
            name: 'draft'
          }
        ],
        stateHandler: completionInputState
      }
    },
    {
      field: 'groupings',
      label: t(translations.pages.modules.grouping()),
      multiselect: {
        options: groupings.map(grouping => {
          return { label: grouping.name, name: parseInt(grouping.id, 10) };
        }),
        stateHandler: groupingInputState,
        customStyle: { width: '200px' }
      },
      hide: subjectType === SubjectType.Fwpm
    }
  ];

  const sortOptions: SortOption[] = [
    {
      label: t(translations.pages.moduleSelectionsDetail.sort.nameAsc()),
      value: { nameSort: 'asc' }
    },
    {
      label: t(translations.pages.moduleSelectionsDetail.sort.nameDesc()),
      value: { nameSort: 'desc' }
    }
  ];

  const onSortChange = sortings => {
    // TODO Change sort State
    invalidateModulesQuery(queryClient);
  };

  type QueryResults = [getGroupings: any, usersQuery: any];

  const SelectionSettingsButtons = () => {
    const anchorRef = useRef(null);
    const [buttonMenuOpen, setButtonMenuOpen] = useState(false);
    return (
      <>
        <Fab
          onClick={e => {
            setButtonMenuOpen(true);
            e.stopPropagation();
            e.preventDefault();
          }}
          size="small"
          style={{ zIndex: 222 }}
          ref={anchorRef}
        >
          <Icon
            className="fas fa-ellipsis-h fa-lg"
            style={{
              width: '1em',
              color: 'black'
            }}
          />
        </Fab>
        <ContextMenu
          open={buttonMenuOpen}
          setOpen={setButtonMenuOpen}
          anchorEl={anchorRef.current}
          sx={{ '& .MuiList-root': { paddingRight: '8px' } }}
        >
          {isSelectionEditable && (
            <>
              <MenuItem
                onClick={e => {
                  openModalHandler();
                  setButtonMenuOpen(false);
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <ListItemIcon style={{ color: 'lightskyblue' }}>
                  <Edit />
                </ListItemIcon>
                <ListItemText
                  primary={t(
                    translations.pages.moduleSelectionsDetail.modulePreview.buttons.edit()
                  )}
                />
              </MenuItem>
              {thisModuleSelectionIsDeleteable(moduleSelection.state) && (
                <MenuItem
                  onClick={e => {
                    setDeleteConfirmOpen(true);
                    setButtonMenuOpen(false);
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  <ListItemIcon>
                    <Delete color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t(
                      translations.pages.moduleSelectionsDetail.modulePreview.buttons.delete()
                    )}
                  />
                </MenuItem>
              )}
            </>
          )}
          <MenuItem
            onClick={e => {
              openTemplateModal();
              setButtonMenuOpen(false);
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <ListItemIcon style={{ color: 'lightskyblue' }}>
              <CreateNewFolder />
            </ListItemIcon>
            <ListItemText
              primary={t(
                translations.pages.moduleSelectionsDetail.modulePreview.buttons.template()
              )}
            />
          </MenuItem>
        </ContextMenu>
      </>
    );
  };

  return (
    <>
      <ConfirmationDialog
        open={isNotTakingPlaceModalOpen}
        title={t(translations.pages.modules.isNotTakingPlaceDialogTitle())}
        onClose={() => {
          setIsNotTakingPlaceModalOpen(false);
        }}
        onConfirm={() => {
          setIsNotTakingPlaceModalOpen(false);
          handleChangeIsTakingPlace();
        }}
      >
        <div>{t(translations.pages.modules.isNotTakingPlaceDialoginfo())}</div>
      </ConfirmationDialog>
      <ErrorBoundary boundaryTag="updateModuleSelectionBoundary">
        <Modal
          open={updateModalOpen}
          onClose={(_event, reason) => {
            if (reason && reason === 'backdropClick') return;
            setCancelModalOpen(true);
          }}
          headline={
            !asTemplate
              ? t(translations.pages.moduleSelections.editModalTitle())
              : t(translations.pages.moduleSelections.createModalTemplateTitle())
          }
          onCancel={(_event, reason) => {
            if (reason && reason === 'backdropClick') return;
            setCancelModalOpen(true);
          }}
        >
          <ModuleSelectionModal
            setDisabledCancel={setDisabledCancel}
            closeHandler={() => cancelModuleSelectionCreation()}
            moduleSelectionToUpdate={moduleSelection}
            fromTemplate={asTemplate}
            subjectType={subjectType}
            refetchQueries={[
              {
                query: MODULES_SELECTION_QUERY,
                variables: { id: parseInt(moduleSelection.id, 10) },
                context: { clientName: 'federation' }
              }
            ]}
          />
        </Modal>
      </ErrorBoundary>
      <ErrorBoundary boundaryTag="addModuleSelection">
        <Modal
          width="full"
          open={addModuleSelectionModal}
          headline={t(translations.pages.moduleSelectionsDetail.modulePreview.addModuleSelection())}
          onCancel={() => setAddModuleSelectionModal(false)}
        >
          <AddModuleToSelectionModal
            moduleSelectionId={moduleSelection.id}
            closeHandler={() => {
              setAddModuleSelectionModal(false);
              queryClient.invalidateQueries({ queryKey: ['modules'] });
            }}
            subjectType={subjectType}
          />
        </Modal>
      </ErrorBoundary>
      <ErrorBoundary boundaryTag="deleteModule">
        <ConfirmationDialog
          title={t(translations.pages.moduleSelectionsDetail.modulePreview.deleteModuleSelection())}
          open={deleteConfirmOpen}
          onClose={() => {
            setDeleteConfirmOpen(false);
          }}
          onConfirm={() => {
            setDeleteConfirmOpen(false);
            handleDeleteSelection();
          }}
        >
          {t(
            translations.pages.moduleSelectionsDetail.modulePreview.deleteModuleSelectionDescription()
          )}
        </ConfirmationDialog>
      </ErrorBoundary>
      <ErrorBoundary boundaryTag="updateModule">
        <Modal
          open={editModalOpen}
          onClose={cancelModuleEditing}
          headline={t(
            translations.pages.modules[!currentlyEditingModule?.id ? 'create' : 'edit'].title[
              subjectType.toLowerCase()
            ]()
          )}
          onCancel={cancelModuleEditing}
          disabledCancel={disabledCancel}
        >
          <ModuleModal
            closeHandler={cancelModuleEditing}
            setDisabledCancel={setDisabledCancel}
            subjectType={subjectType}
            module={currentlyEditingModule}
            courses={courses}
          />
        </Modal>
      </ErrorBoundary>
      <ConfirmationDialog
        open={deleteModalOpen}
        title={t(translations.pages.modules.deleteConfirm.title())}
        onClose={() => {
          setDeleteModalOpen(false);
        }}
        onConfirm={() => {
          setDeleteModalOpen(false);
          handleDelete();
        }}
      >
        <div>{t(translations.pages.modules.deleteConfirm.message())}</div>
      </ConfirmationDialog>
      <ConfirmationDialog
        open={cancelModalOpen}
        title={t(translations.common.cancelConfirm.title())}
        onClose={() => {
          setCancelModalOpen(false);
        }}
        onConfirm={() => {
          setUpdateModalOpen(false);
          setAsTemplate(false);
          setCancelModalOpen(false);
        }}
      >
        <div>{t(translations.common.cancelConfirm.message())}</div>
      </ConfirmationDialog>
      <Box className="previewTab">
        <GQLGuard
          query={[getGroupings]}
          onError={error => {
            if (error?.graphQLErrors[0]?.extensions?.status === 404) {
              dispatch(
                AlertActions.error(`${t(translations.pages.modules.alerts.failedLoading())}`)
              );
            } else {
              dispatch(
                AlertActions.error(
                  `${t(translations.pages.modules.alerts.failedLoading())}: ${
                    error?.message ?? JSON.stringify(error)
                  }`
                )
              );
            }
            navigate('/', { replace: true });
            return null;
          }}
        >
          {([groupingsData]: QueryResults) => {
            return (
              <>
                <Box className="previewTab__heading-box">
                  <Box className="previewTab__heading-box__information">
                    <Grid container justifyContent="space-between">
                      <Typography component="h2">
                        {t(
                          translations.pages.moduleSelectionsDetail.modulePreview.information.title()
                        )}
                      </Typography>
                      {allowedToEdit && <SelectionSettingsButtons />}
                    </Grid>

                    <Divider />
                    <Box m={2}>
                      <Grid container>
                        <Grid item xs={6}>
                          <DateBox
                            endDate={moduleSelection.endDate}
                            startDate={moduleSelection.startDate}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Typography textAlign="right">
                            {t(
                              translations.pages.moduleSelectionsDetail.modulePreview.selectionState()
                            )}
                            : {displayStates(moduleSelection.state)}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid container justifyContent={'space-between'}>
                        <Grid item className="previewTab__heading-box__information__headline-entry">
                          <EntryWithHeadline
                            entry={`${t(
                              translations.pages.moduleSelectionsDetail.modulePreview.information.until()
                            )} ${
                              moduleSelection.dischargeByStudentsUntil
                                ? new Date(
                                    moduleSelection.dischargeByStudentsUntil
                                  ).toLocaleDateString('de-DE', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: 'numeric',
                                    minute: 'numeric'
                                  })
                                : '-/-'
                            }`}
                            headline={t(
                              translations.pages.moduleSelectionsDetail.modulePreview.information.dischargeByStudentsUntil()
                            )}
                          />
                        </Grid>
                        <Grid item className="previewTab__heading-box__information__headline-entry">
                          <EntryWithHeadline
                            entry={
                              moduleSelection.contentVisibleFrom
                                ? new Date(moduleSelection.contentVisibleFrom).toLocaleDateString(
                                    'de-DE',
                                    {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                      hour: 'numeric',
                                      minute: 'numeric'
                                    }
                                  )
                                : '-/-'
                            }
                            headline={t(
                              translations.pages.moduleSelectionsDetail.modulePreview.information.contentVisibleFrom()
                            )}
                          />
                        </Grid>
                        <Grid item className="previewTab__heading-box__information__headline-entry">
                          <EntryWithHeadline
                            entry={
                              moduleSelection.publishedAt
                                ? new Date(moduleSelection.publishedAt).toLocaleDateString(
                                    'de-DE',
                                    {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                      hour: 'numeric',
                                      minute: 'numeric'
                                    }
                                  )
                                : '-/-'
                            }
                            headline={t(
                              translations.pages.moduleSelectionsDetail.modulePreview.information.publishedAt()
                            )}
                          />
                        </Grid>
                      </Grid>
                      {subjectType === SubjectType.Awpm && (
                        <div className="previewTab__heading-box__information__allCommunities">
                          <Checkbox
                            color="primary"
                            disabled
                            checked={moduleSelection.activeInAllCommunities}
                          />
                          <Typography>
                            {t(
                              translations.pages.moduleSelectionsDetail.modulePreview.information.activeInAllCommunities()
                            )}
                          </Typography>
                        </div>
                      )}
                      <Accordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')}>
                        <AccordionSummary
                          expandIcon={<Icon className="fas fa-caret-down" />}
                          aria-controls="panel1bh-content"
                          id="panel1bh-header"
                        >
                          <Typography>
                            {t(
                              translations.pages.moduleSelectionsDetail.modulePreview.information.generalInfo()
                            )}
                          </Typography>
                        </AccordionSummary>
                        <Divider />
                        <AccordionDetails>
                          <ReactQuill
                            readOnly={true}
                            theme="bubble"
                            value={moduleSelection.generalNotes}
                          />
                        </AccordionDetails>
                      </Accordion>
                      {!!moduleSelection.electionTip && (
                        <Accordion
                          expanded={expanded === 'panel2'}
                          onChange={handleChange('panel2')}
                        >
                          <AccordionSummary
                            expandIcon={<Icon className="fas fa-caret-down" />}
                            aria-controls="panel2bh-content"
                            id="panel2bh-header"
                          >
                            <Typography>
                              {t(
                                translations.pages.moduleSelectionsDetail.modulePreview.information.electionTips()
                              )}
                            </Typography>
                          </AccordionSummary>
                          <Divider />
                          <AccordionDetails>
                            <ReactQuill
                              readOnly={true}
                              theme="bubble"
                              value={moduleSelection.electionTip}
                            />
                          </AccordionDetails>
                        </Accordion>
                      )}
                      {moduleSelection?.attachments?.map(attachment => (
                        <Link
                          onClick={() =>
                            handleDownload(
                              attachment.originalname,
                              `/module-selections/${moduleSelection.id}/attachments/${attachment.id}/download`
                            )
                          }
                          key={attachment.name}
                          style={{ cursor: 'pointer' }}
                        >
                          <CloudDownload /> {attachment.originalname}
                        </Link>
                      ))}
                    </Box>
                  </Box>
                  {isSelectionEditable && (
                    <Grid container item xs={6} direction={'row'} justifyContent={'space-between'}>
                      <Grid item>
                        <SelectionAdminButtons
                          modules={modules}
                          moduleSelection={moduleSelection}
                          setTab={setTab}
                        />
                      </Grid>
                      <Grid item style={{ padding: '1em' }}>
                        <WatchlistAddButton
                          categoryName={'MODULE_SELECTION'}
                          description={moduleSelection.generalNotes || ''}
                          title={moduleSelection.title}
                          size={'medium'}
                          date={moduleSelection.startDate}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Box>
                <Divider />
                <Box className="previewTab__courses">
                  <Typography component="h2">
                    {t(translations.pages.moduleSelectionsDetail.modulePreview.modulesTitle())}
                  </Typography>
                  {isLoading ? (
                    <CircularProgress />
                  ) : (
                    <Grid container style={{ width: '100%' }}>
                      <TilesOrListView
                        data={modules || []}
                        height={'70vh'}
                        filters={richlistFilter(groupingsData.getGroupings)}
                        onFilterChange={variables => {
                          // TODO Change Filter State
                          // refetchModules(handleModulesFilterQuery(variables))
                        }}
                        sortOptions={sortOptions}
                        onSortChange={onSortChange}
                        controlElements={
                          isSelectionEditable && (
                            <Grid item>
                              <Button
                                startIcon={
                                  <Icon className="fas fa-plus" style={{ color: 'white' }} />
                                }
                                className="previewTab__courses__add-button"
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                  setAddModuleSelectionModal(true);
                                }}
                              >
                                {t(
                                  translations.pages.moduleSelectionsDetail.modulePreview.addModuleSelection()
                                )}
                              </Button>
                            </Grid>
                          )
                        }
                        getRowClassName={props =>
                          !props.row.isTakingPlace ? 'crossed-out-row' : ''
                        }
                        renderTile={module => (
                          <ModuleItem
                            module={module}
                            onDelete={() => invalidateModulesQuery(queryClient)}
                            onEdit={() => invalidateModulesQuery(queryClient)}
                            subjectType={subjectType}
                            deactivateButtons={!isSelectionEditable}
                            disabled={!module.isTakingPlace}
                            selection={moduleSelection}
                          />
                        )}
                        columns={
                          isSelectionEditable
                            ? columns
                            : columns.filter((_, index) => index < columns.length - 1)
                        }
                        onRowClick={event => {
                          navigate(
                            `/${event.row.subjectType.toLowerCase()}/modules/${event.row.id}`,
                            { state: { back: currentURL } }
                          );
                        }}
                        columnVisibilityModel={{ grouping: subjectType !== SubjectType.Fwpm }}
                        explanatoryLegend={
                          modules?.find(m => !m.isTakingPlace) && <ExplanatoryLegend />
                        }
                      />
                    </Grid>
                  )}
                </Box>
              </>
            );
          }}
        </GQLGuard>
      </Box>
    </>
  );
}
